import React, { useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { DocumentUpload } from './DocumentUpload';
import { uploadContract } from '../../services/api';

export interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: (documentId: string) => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
}) => {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleUpload = useCallback(async (file: File): Promise<{ success: boolean; documentId?: string; error?: string }> => {
    try {
      const response = await uploadContract(file);

      // Notify parent of successful upload
      if (onUploadComplete) {
        onUploadComplete(response.documentId);
      }

      return { success: true, documentId: response.documentId };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      return { success: false, error: errorMessage };
    }
  }, [onUploadComplete]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="upload-modal-title"
      role="dialog"
      aria-modal="true"
      data-testid="upload-modal"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-neutral-900/50 backdrop-blur-sm transition-opacity"
        onClick={handleBackdropClick}
        data-testid="upload-modal-backdrop"
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto animate-in fade-in zoom-in-95 duration-200"
          data-testid="upload-modal-content"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-neutral-100">
            <h2
              id="upload-modal-title"
              className="text-lg font-semibold text-neutral-900"
              data-testid="upload-modal-title"
            >
              Upload Contract Document
            </h2>
            <button
              onClick={onClose}
              className="btn-icon-sm btn-ghost text-neutral-400 hover:text-neutral-600"
              data-testid="upload-modal-close"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <DocumentUpload onUpload={handleUpload} />

            <div className="mt-5 p-4 bg-neutral-50 rounded-lg" data-testid="upload-info">
              <p className="text-sm font-medium text-neutral-700 mb-2">Supported document types:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-600">
                <li>PDF documents (.pdf)</li>
                <li>Microsoft Word documents (.docx)</li>
              </ul>
              <p className="mt-3 text-xs text-neutral-400">
                Documents will be processed to extract contract structure, articles, and clauses for analysis.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="form-actions p-5 border-t border-neutral-100 bg-neutral-50/50 rounded-b-2xl">
            <button
              onClick={onClose}
              className="btn-outline"
              data-testid="upload-modal-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
